import { Action, createReducer, on } from '@ngrx/store';
import * as Actions from './general.actions';
import { VehicleSelection } from '@core/models/swagger/vehicleSelection';
import { ComboBoxStatus, UserCategory, Vehicle } from '@core/models/swagger';
import { PreviousInsuranceFinishEnum, QuestionEnum } from '@global/enums';
import { QbItaParameterResult } from '@fwmovilidad/qb-home-ita-components-lib';
import { Recommendation } from '@core/models/swagger/recommendation';
import { Quoting } from '@core/models/swagger/quoting';

export interface State {
  vehicleSelection: VehicleSelection | null;
  uploadedImage: string | null;
  brand: ComboBoxStatus | null;
  manufactureYear: ComboBoxStatus | null;
  model: ComboBoxStatus | null;
  fuel: ComboBoxStatus | null;
  carType: ComboBoxStatus | null;
  horsePower: ComboBoxStatus | null;
  possibleVehicles: Vehicle[] | null;
  selectedVehicle: number | null;
  prevUrl: string | null;
  insurerTakerCategory: UserCategory | null;
  otherVehicles: boolean | null;
  priceInfo: any | null;
  bankAccountHolderAuthorization: boolean | null;
  vehicleOwnerCategory: UserCategory | null;
  bankAccountHolderCategory: UserCategory | null;
  previousInsuranceFinish: PreviousInsuranceFinishEnum | null;
  hasPreviousClaims: QuestionEnum | null;
  gapSelection: boolean | null;
  postcode: string | null;
  deductibleOptions: QbItaParameterResult | null;
  paymentMethods: QbItaParameterResult | null;
  progressId: number;
  fullOperationId: string | null;
  insuranceModalitiesOffered: string | null;
  insurancePricesOffered: string | null;
  previousInsuranceBrand: string | null;
  defaultPrice: string | null;
  addonNamesSelected: string | null;
  addonPricesSelected: string | null;
  analyticBrand: string | null;
  analyticModel: string | null;
  referralCodeUrl: string | null;
  lastPromotionCodeValidated: string | null;
  vehicleDoors: number | null;
  recommendations: Recommendation | null;
  quotation: Quoting | null;
  carInsurers: QbItaParameterResult[] | null;
}

export const featureKey = 'flowDataGeneral';

export const initialState: State = {
  vehicleSelection: null,
  uploadedImage: null,
  brand: null,
  manufactureYear: null,
  model: null,
  fuel: null,
  carType: null,
  horsePower: null,
  possibleVehicles: null,
  selectedVehicle: null,
  prevUrl: null,
  insurerTakerCategory: null,
  otherVehicles: null,
  priceInfo: null,
  bankAccountHolderAuthorization: null,
  vehicleOwnerCategory: null,
  bankAccountHolderCategory: null,
  previousInsuranceFinish: null,
  hasPreviousClaims: null,
  gapSelection: null,
  postcode: null,
  deductibleOptions: null,
  paymentMethods: null,
  progressId: 0,
  fullOperationId: null,
  insuranceModalitiesOffered: null,
  insurancePricesOffered: null,
  previousInsuranceBrand: null,
  defaultPrice: null,
  addonNamesSelected: null,
  addonPricesSelected: null,
  analyticBrand: null,
  analyticModel: null,
  referralCodeUrl: null,
  lastPromotionCodeValidated: null,
  vehicleDoors: null,
  recommendations: null,
  quotation: null,
  carInsurers: null,
};

export function generalReducer(state: State | undefined, action: Action): State {
  return createReducer(
    initialState,
    on(Actions.ResetGeneral, (state, {}) => ({
      ...state,
      vehicleSelection: null,
      uploadedImage: null,
      brand: null,
      manufactureYear: null,
      model: null,
      fuel: null,
      carType: null,
      horsePower: null,
      possibleVehicles: null,
      selectedVehicle: null,
      prevUrl: null,
      insurerTakerCategory: null,
      otherVehicles: null,
      priceInfo: null,
      bankAccountHolderAuthorization: null,
      vehicleOwnerCategory: null,
      bankAccountHolderCategory: null,
      previousInsuranceFinish: null,
      hasPreviousClaims: null,
      gapSelection: null,
      postcode: null,
      deductibleOptions: null,
      paymentMethods: null,
      progressId: 0,
      fullOperationId: null,
      insuranceModalitiesOffered: null,
      insurancePricesOffered: null,
      previousInsuranceBrand: null,
      defaultPrice: null,
      addonNamesSelected: null,
      addonPricesSelected: null,
      analyticBrand: null,
      analyticModel: null,
      referralCodeUrl: null,
      lastPromotionCodeValidated: null,
      vehicleDoors: null,
      recommendations: null,
      quotation: null,
      carInsurers: null,
    })),
    on(Actions.UpdateVehicleSelection, (state, { data }) => ({
      ...state,
      vehicleSelection: data,
    })),
    on(Actions.UpdateUploadedImage, (state, { data }) => ({
      ...state,
      uploadedImage: data,
    })),
    on(Actions.UpdateBrand, (state, { data }) => ({
      ...state,
      brand: data,
    })),
    on(Actions.UpdateManufactureYear, (state, { data }) => ({
      ...state,
      manufactureYear: data,
    })),
    on(Actions.UpdateModel, (state, { data }) => ({
      ...state,
      model: data,
    })),
    on(Actions.UpdateFuel, (state, { data }) => ({
      ...state,
      fuel: data,
    })),
    on(Actions.UpdateCarType, (state, { data }) => ({
      ...state,
      carType: data,
    })),
    on(Actions.UpdateHorsePower, (state, { data }) => ({
      ...state,
      horsePower: data,
    })),
    on(Actions.UpdatePossibleVehicles, (state, { data }) => ({
      ...state,
      possibleVehicles: data,
    })),
    on(Actions.UpdateSelectedVehicle, (state, { data }) => ({
      ...state,
      selectedVehicle: data,
    })),
    on(Actions.UpdatePrevUrl, (state, { data }) => ({
      ...state,
      prevUrl: data,
    })),
    on(Actions.UpdateInsurerTakerCategory, (state, { data }) => ({
      ...state,
      insurerTakerCategory: data,
    })),
    on(Actions.UpdateOtherVehicles, (state, { data }) => ({
      ...state,
      otherVehicles: data,
    })),
    on(Actions.UpdatePriceInfo, (state, { data }) => ({
      ...state,
      priceInfo: data,
    })),
    on(Actions.UpdateBankAccountHolderAuthorization, (state, { data }) => ({
      ...state,
      bankAccountHolderAuthorization: data,
    })),
    on(Actions.UpdateVehicleOwnerCategory, (state, { data }) => ({
      ...state,
      vehicleOwnerCategory: data,
    })),
    on(Actions.UpdateBankAccountHolderCategory, (state, { data }) => ({
      ...state,
      bankAccountHolderCategory: data,
    })),
    on(Actions.UpdatePreviousInsuranceFinish, (state, { data }) => ({
      ...state,
      previousInsuranceFinish: data,
    })),
    on(Actions.UpdateHasPreviousClaims, (state, { data }) => ({
      ...state,
      hasPreviousClaims: data,
    })),
    on(Actions.UpdateGapSelection, (state, { data }) => ({
      ...state,
      gapSelection: data,
    })),
    on(Actions.UpdatePostcode, (state, { data }) => ({
      ...state,
      postcode: data,
    })),
    on(Actions.UpdateDeductibleOptions, (state, { data }) => ({
      ...state,
      deductibleOptions: data,
    })),
    on(Actions.UpdatePaymentMethods, (state, { data }) => ({
      ...state,
      paymentMethods: data,
    })),
    on(Actions.UpdateProgressId, (state, { data }) => ({
      ...state,
      progressId: data,
    })),
    on(Actions.UpdateFullOperationId, (state, { data }) => ({
      ...state,
      fullOperationId: data,
    })),
    on(Actions.UpdateInsuranceModalitiesOffered, (state, { data }) => ({
      ...state,
      insuranceModalitiesOffered: data,
    })),
    on(Actions.UpdateInsurancePricesOffered, (state, { data }) => ({
      ...state,
      insurancePricesOffered: data,
    })),
    on(Actions.UpdatePreviousInsuranceBrand, (state, { data }) => ({
      ...state,
      previousInsuranceBrand: data,
    })),
    on(Actions.UpdateDefaultPrice, (state, { data }) => ({
      ...state,
      defaultPrice: data,
    })),
    on(Actions.UpdateAddonNamesSelected, (state, { data }) => ({
      ...state,
      addonNamesSelected: data,
    })),
    on(Actions.UpdateAddonPricesSelected, (state, { data }) => ({
      ...state,
      addonPricesSelected: data,
    })),
    on(Actions.UpdateAnalyticBrand, (state, { data }) => ({
      ...state,
      analyticBrand: data,
    })),
    on(Actions.UpdateAnalyticModel, (state, { data }) => ({
      ...state,
      analyticModel: data,
    })),
    on(Actions.UpdateReferralCodeUrl, (state, { data }) => ({
      ...state,
      referralCodeUrl: data,
    })),
    on(Actions.UpdateLastPromotionCodeValidated, (state, { data }) => ({
      ...state,
      lastPromotionCodeValidated: data,
    })),
    on(Actions.UpdateVehicleDoors, (state, { data }) => ({
      ...state,
      vehicleDoors: data,
    })),
    on(Actions.UpdateRecommendations, (state, { data }) => ({
      ...state,
      recommendations: data,
    })),
    on(Actions.UpdateQuotation, (state, { data }) => ({
      ...state,
      quotation: data,
    })),
    on(Actions.UpdateCarInsurers, (state, { data }) => ({
      ...state,
      carInsurers: data,
    }))
  )(state, action);
}
